import { Button, Select } from '@mantine/core';
import { KeyboardEvent, MouseEvent, useState } from 'react';

import ConditionalTooltip from '@/components/ConditionalTooltip.tsx';
import { CollectionScopeSelector } from '@/components/collection-scope-selector.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { CollectionActionScope } from '@/models/appointment/Appointment.ts';
import {
    APPOINTMENT_CANCEL_OPTION_TRANSLATIONS,
    CancelAppointmentOptions,
} from '@/models/appointment/CancelAppointmentOptions.ts';

import { cn } from '@/utils/styleUtils.ts';

interface Props {
    onDelete: (selectedScope: CollectionActionScope | undefined, cancelOption: CancelAppointmentOptions) => void;
    onCancel: () => void;
    onlyNoShow?: boolean;
    isSeriesMode?: boolean;
    isChainMode?: boolean;
    chainFutureAppointmentCount?: number;
}

const handleClick = (e: MouseEvent): void => {
    // Prevent click events from bubbling up to parent components
    e.stopPropagation();
};

const handleKeyDown = (e: KeyboardEvent): void => {
    // Prevent keyboard events from bubbling up to parent components
    e.stopPropagation();
};

export const DeleteAppointment = ({
    onDelete,
    onCancel,
    onlyNoShow,
    isSeriesMode,
    isChainMode,
    chainFutureAppointmentCount,
}: Props) => {
    const [deleteReason, setDeleteReason] = useState<CancelAppointmentOptions | undefined>(undefined);
    const [selectedScope, setSelectedScope] = useState<CollectionActionScope>(CollectionActionScope.SINGLE);

    const handleCancel = (): void => {
        onCancel();
        setDeleteReason(undefined);
    };

    const handleDelete = (): void => {
        if (deleteReason) {
            if (isSeriesMode) {
                onDelete(selectedScope, deleteReason);
            } else if (isChainMode) {
                onDelete(CollectionActionScope.ALL_FUTURE, deleteReason);
            } else {
                onDelete(undefined, deleteReason);
            }
        }
    };

    const isAllFutureScope = isChainMode || (isSeriesMode && selectedScope === CollectionActionScope.ALL_FUTURE);

    const selectOptions = Object.entries(APPOINTMENT_CANCEL_OPTION_TRANSLATIONS)
        .map(([value, label]) => {
            // Filter logic: if onlyNoShow is true, show only NO_SHOW; NO_SHOW is hidden when cancelling all future appointments (chain mode or series with ALL_FUTURE scope)
            if (
                (onlyNoShow && (value as CancelAppointmentOptions) !== CancelAppointmentOptions.NO_SHOW) ||
                (isAllFutureScope && (value as CancelAppointmentOptions) === CancelAppointmentOptions.NO_SHOW)
            ) {
                return undefined;
            }
            return {
                value, // Actual enum value
                label, // German translation
            };
        })
        .filter((option) => !!option);

    const handleScopeChange = (scope: CollectionActionScope): void => {
        setSelectedScope(scope);
        if (scope === CollectionActionScope.ALL_FUTURE && deleteReason === CancelAppointmentOptions.NO_SHOW) {
            setDeleteReason(undefined);
        }
    };

    // eslint-disable-next-line unicorn/no-null
    const selectValue = deleteReason ?? null; // Mantine Select expects `null` to represent an empty value

    const getCancelReasonTitle = (): string => {
        if (isChainMode) return 'Wählen Sie einen Grund zum Stornieren der Terminkette';
        if (isSeriesMode && selectedScope === CollectionActionScope.ALL_FUTURE)
            return 'Wählen Sie einen Grund zum Stornieren der Terminserie';
        return 'Wählen Sie einen Grund zum Stornieren des Termins';
    };

    return (
        <div>
            {isChainMode && !onlyNoShow && (
                <div>
                    <Paragraph className="mb-1">
                        Alle zukünftigen Termine in dieser Terminkette werden storniert. Vergangene Termine bleiben im
                        Kalender erhalten.
                    </Paragraph>
                    {!!chainFutureAppointmentCount && (
                        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2">
                            <Paragraph className="text-sm text-red-700">
                                Anzahl der zu stornierenden Termine: {chainFutureAppointmentCount}
                            </Paragraph>
                        </div>
                    )}
                </div>
            )}
            {isSeriesMode && !onlyNoShow && (
                <div className="mb-4">
                    <Paragraph className="mb-2">Dieser Termin ist Teil einer Serie. Was möchten Sie löschen?</Paragraph>
                    <CollectionScopeSelector selectedScope={selectedScope} onChange={handleScopeChange} />
                </div>
            )}
            <div
                className="mt-2 flex w-full flex-col items-start gap-4"
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={0}
            >
                <div className="flex w-full flex-col gap-1">
                    <Paragraph>{getCancelReasonTitle()}</Paragraph>
                    <Select
                        label="Grund"
                        placeholder="Bitte auswählen"
                        value={selectValue}
                        onChange={(value) => setDeleteReason(value as CancelAppointmentOptions)}
                        data={selectOptions}
                    />

                    <div className="mt-2 flex justify-end gap-2">
                        <Button variant="outline" onClick={handleCancel}>
                            Abbrechen
                        </Button>
                        {/* EN: please choose a reason */}
                        <ConditionalTooltip label={deleteReason ? undefined : 'Bitte einen Grund wählen.'}>
                            <Button
                                disabled={!deleteReason}
                                className={cn(deleteReason && 'text-white hover:bg-red-600 hover:text-white')}
                                color="red"
                                onClick={handleDelete}
                            >
                                Löschen
                            </Button>
                        </ConditionalTooltip>
                    </div>
                </div>
            </div>
        </div>
    );
};
